// ─── Fiche INFO d'un calendrier (lecture seule) ───────────────────
// Standard app-wide : le clic ouvre la FICHE, jamais une action. On y voit à
// quel ESPACE le calendrier appartient, qui le possède et ce qu'il est ; les
// actions (rendre actif, éditer) sont en en-tête.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Feuille, useUI, useT, useAuth, useEspace, HIT, transfererElement, confirmerChoix, db, CarteInfos } from '@mop/core';
import { doc, getDoc } from 'firebase/firestore';
import { TYPES_CALENDRIER } from './services/calendriersEspace';

export default function FicheCalendrier({ cal, actif, onFermer, onRendreActif, onEditer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { user } = useAuth();
  const { espaces } = useEspace();

  const [choix, setChoix] = useState(false);   // calque « déplacer / donner »
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [nomRegistre, setNomRegistre] = useState('');

  const uidProprio = cal ? (cal.creePar || cal.ownerUid) : null;
  useEffect(() => {
    if (!uidProprio) { setNomRegistre(''); return; }
    getDoc(doc(db, 'profilsPublics', uidProprio))
      .then(sn => setNomRegistre(sn.exists() ? (sn.data().prenom || '') : ''))
      .catch(() => setNomRegistre(''));
  }, [uidProprio]);

  if (!cal) return null;
  const espace = (espaces || []).find(e => e.id === cal.espaceId);
  const proprio = (cal.creePar || cal.ownerUid) === user?.uid;
  // Nom du propriétaire : lu dans le REGISTRE public (voir le useEffect
  // ci-dessus) plutôt que dans le `creeParNom` figé à la création, qui devient
  // faux dès que la personne se renomme. L'e-mail n'est pas exposé :
  // `profilsPublics` ne porte que le prénom, par choix de confidentialité.
  const rows = [
    { ic: '🏠', lbl: t('jetons.espaceDe'), val: espace?.nom || cal.espaceId || t('jetons.espaceInconnu') },
    { ic: '👤', lbl: t('jetons.proprietaire'), val: proprio ? t('jetons.moi') : (nomRegistre || cal.creeParNom || t('jetons.autreMembre')) },
    { ic: '🗂️', lbl: t('calendrier.ficheType'),
      val: TYPES_CALENDRIER.find(tc => tc.id === cal.type) ? t('calendrier.typeCal_' + cal.type) : t('calendrier.titre') },
    { ic: '★', lbl: t('calendrier.actif'), val: actif ? t('commun.oui') : t('commun.non') },
  ];

  // Espaces où je suis MEMBRE : on ne dépose pas un élément chez des inconnus.
  const cibles = (espaces || []).filter(e => e.id !== cal.espaceId && (e.membres || {})[user?.uid]);
  const estProprioDe = (e) => (e.membres || {})[user?.uid] === 'proprietaire';

  const transferer = (e) => {
    const donne = !estProprioDe(e);
    const faire = async () => {
      setBusy(true); setErr('');
      try { await transfererElement(`calendrier:${cal.id}`, e.id); setChoix(false); onFermer?.(); }
      catch (x) { setErr(x?.message || String(x)); }
      finally { setBusy(false); }
    };
    // DONNER est irréversible de mon côté : on l'annonce sans détour.
    if (!donne) return faire();
    confirmerChoix(t('calendrier.donnerTitre'), t('calendrier.donnerMsg', { espace: e.nom }), [
      { text: t('calendrier.donnerConfirmer'), style: 'destructive', onPress: faire },
      { text: t('commun.annuler'), style: 'cancel' },
    ]);
  };

  return (
    <Feuille visible={!!cal} titre={cal.nom || t('calendrier.titre')} emoji={cal.emoji || '📅'} hauteur={0.82}
      onFermer={onFermer}
      action={onEditer ? { icone: '✏️', label: t('commun.modifier'), onPress: onEditer } : undefined}
      boutons={(
        <>
          {/* Déplacer / donner : action LOCALE du propriétaire, en icône. */}
          {proprio && (
            <TouchableOpacity hitSlop={HIT} onPress={() => { setChoix(true); setErr(''); }}
              accessibilityLabel={t('calendrier.changerEspace')}>
              <Text style={s.actionTxt}>🏠</Text>
            </TouchableOpacity>
          )}
          {!actif && !!onRendreActif && (
            <TouchableOpacity hitSlop={HIT} onPress={onRendreActif} accessibilityLabel={t('calendrier.rendreActif')}>
              <Text style={s.actionTxt}>★</Text>
            </TouchableOpacity>
          )}
        </>
      )}>
      <CarteInfos rows={rows} />
      {!!err && <Text style={s.err}>⚠️ {err}</Text>}
      <Text style={s.aide}>{t('calendrier.ficheCalAide')}</Text>

      {/* Choix de l'espace : CALQUE absolu (jamais une 2e Modal dans une Feuille). */}
      {choix && (
        <View style={s.calque}>
          <TouchableOpacity style={StyleSheet.absoluteFill} activeOpacity={1} onPress={() => setChoix(false)} />
          <View style={s.calqueCarte}>
            <Text style={s.calqueTitre}>🏠 {t('calendrier.changerEspace')}</Text>
            <Text style={s.aide}>{t('calendrier.changerEspaceAide')}</Text>
            {busy && <ActivityIndicator size="small" color={UI.accent} />}
            {cibles.length === 0
              ? <Text style={s.aide}>{t('calendrier.aucunAutreEspace')}</Text>
              : cibles.map(e => (
                <TouchableOpacity key={e.id} style={s.calqueLigne} activeOpacity={0.7}
                  disabled={busy} onPress={() => transferer(e)}>
                  <Text style={s.rowVal2} numberOfLines={1}>{e.emoji || '🏠'} {e.nom}</Text>
                  <Text style={s.calqueSous}>
                    {estProprioDe(e) ? t('calendrier.deplacerSimple') : t('calendrier.donnerEtiquette')}
                  </Text>
                </TouchableOpacity>
              ))}
            <TouchableOpacity style={s.calqueFermer} onPress={() => setChoix(false)}>
              <Text style={s.calqueFermerTxt}>{t('commun.annuler')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  actionTxt: { fontSize: 19, color: UI.accent, fontWeight: '800' },
  aide: { fontSize: 11.5, color: UI.faint, lineHeight: 16, marginTop: 12 },
  err: { fontSize: 12.5, color: UI.danger, fontWeight: '700', marginTop: 10 },
  calque: { ...StyleSheet.absoluteFillObject, justifyContent: 'flex-end' },
  calqueCarte: { backgroundColor: UI.card, borderTopLeftRadius: 18, borderTopRightRadius: 18, padding: 16 },
  calqueTitre: { fontSize: 16, fontWeight: '900', color: UI.ink, marginBottom: 2 },
  calqueLigne: { paddingVertical: 12, borderTopWidth: 0.5, borderTopColor: UI.border },
  calqueSous: { fontSize: 11.5, color: UI.muted, marginTop: 2 },
  rowVal2: { fontSize: 14, fontWeight: '700', color: UI.ink },
  calqueFermer: { alignItems: 'center', paddingVertical: 12, marginTop: 6 },
  calqueFermerTxt: { fontSize: 13.5, color: UI.accent, fontWeight: '800' },
});
